import React, { useState, useEffect } from 'react';
import { FiX, FiSave, FiFileText } from 'react-icons/fi';
import { Editor } from '@tinymce/tinymce-react';
import { editorConfig } from '../../utils/editor-config';

type PageStatus = 'active' | 'draft' | 'inactive';
type PageType = 'privacy' | 'terms' | 'cookie' | 'refund' | 'about' | 'faq' | 'custom';

interface Page {
    _id?: string;
    title: string;
    slug: string;
    content: string;
    status: PageStatus;
    page_type: PageType;
    created_at?: string;
    updated_at?: string;
}

interface OffCanvasFormProps {
    show: boolean;
    onClose: () => void;
    page?: Page;
    onSubmit: (data: Page) => void;
}

const OffCanvasForm: React.FC<OffCanvasFormProps> = ({ show, onClose, page, onSubmit }) => {
    const isEditMode = !!page;

    const [formData, setFormData] = useState<Page>({
        title: '',
        slug: '',
        content: '',
        status: 'draft',
        page_type: 'custom'
    });

    const [errors, setErrors] = useState<Record<string, string>>({});
    const [touched, setTouched] = useState<Record<string, boolean>>({});

    useEffect(() => {
        if (page) {
            setFormData({
                title: page.title || '',
                slug: page.slug || '',
                content: page.content || '',
                status: page.status || 'draft',
                page_type: page.page_type || 'custom',
            });
        }
    }, [page]);

    useEffect(() => {
        if (!touched.slug && formData.title) {
            const generatedSlug = formData.title
                .replace(/&/g, 'and')
                .toLowerCase()
                .replace(/[^a-z0-9]+/g, '-')
                .replace(/^-|-$/g, '');

            setFormData(prev => ({
                ...prev,
                slug: generatedSlug
            }));
        }
    }, [formData.title, touched.slug]);

    const validate = (): boolean => {
        const newErrors: Record<string, string> = {};

        if (!formData.title.trim()) {
            newErrors.title = 'Title is required';
        }

        if (!formData.slug.trim()) {
            newErrors.slug = 'Slug is required';
        } else if (!/^[a-z0-9-]+$/.test(formData.slug)) {
            newErrors.slug = 'Slug can only contain lowercase letters, numbers, and hyphens';
        }

        if (!formData.content.trim()) {
            newErrors.content = 'Content is required';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        setTouched(prev => ({
            ...prev,
            [name]: true
        }));
    };

    const handleEditorChange = (content: string) => {
        setFormData(prev => ({
            ...prev,
            content
        }));
        setTouched(prev => ({
            ...prev,
            content: true
        }));
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (validate()) {
            onSubmit(formData);
            // Reset form after submission if not in edit mode
            if (!isEditMode) {
                setFormData({
                    title: '',
                    slug: '',
                    content: '',
                    status: 'draft',
                    page_type: 'custom'
                });
            }
            onClose();
        }
    };

    const handleClose = () => {
        onClose();
        setErrors({});
    };

    return (
        <>
            {/* Backdrop */}
            <div
                className={`fixed inset-0 bg-black bg-opacity-50 z-40 transition-opacity duration-300 ${show ? 'opacity-100' : 'opacity-0 pointer-events-none'
                    }`}
                onClick={handleClose}
            />

            {/* Offcanvas */}
            <div className={`fixed top-0 right-0 h-full w-full max-w-6xl bg-white shadow-xl z-50 transform transition-transform duration-300 ease-in-out ${show ? 'translate-x-0' : 'translate-x-full'
                }`}>

                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-gray-200">
                    <h2 className="text-xl font-semibold text-gray-900 flex items-center">
                        <FiFileText className="mr-2" />
                        {isEditMode ? 'Edit Page' : 'Create New Page'}
                    </h2>
                    <button
                        onClick={handleClose}
                        className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                    >
                        <FiX className="w-5 h-5" />
                    </button>
                </div>

                {/* Form */}
                <div className="flex flex-col h-full">
                    <div className="flex-1 overflow-y-auto p-6 space-y-6">
                        <form onSubmit={handleSubmit} className="space-y-6">
                            {/* Basic Information */}
                            <div className="space-y-4">
                                <h3 className="text-lg font-medium text-gray-900 border-b pb-2">Page Information</h3>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Title *
                                    </label>
                                    <input
                                        type="text"
                                        name="title"
                                        id="title"
                                        value={formData.title}
                                        onChange={handleChange}
                                        className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${errors.title ? 'border-red-500' : 'border-gray-300'
                                            }`}
                                        placeholder="Enter page title"
                                    />
                                    {errors.title && <p className="text-red-500 text-xs mt-1">{errors.title}</p>}
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Slug *
                                    </label>
                                    <input
                                        type="text"
                                        name="slug"
                                        id="slug"
                                        value={formData.slug}
                                        onChange={handleChange}
                                        className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${errors.slug ? 'border-red-500' : 'border-gray-300'
                                            }`}
                                        placeholder="Enter page slug"
                                    />
                                    {errors.slug && <p className="text-red-500 text-xs mt-1">{errors.slug}</p>}
                                    <p className="mt-1 text-xs text-gray-500">
                                        URL: /page/{formData.slug || 'your-slug'}
                                    </p>
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Page Type
                                        </label>
                                        <select
                                            name="page_type"
                                            value={formData.page_type}
                                            onChange={handleChange}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                                        >
                                            <option value="privacy">Privacy Policy</option>
                                            <option value="terms">Terms of Service</option>
                                            <option value="cookie">Cookie Policy</option>
                                            <option value="refund">Refund Policy</option>
                                            <option value="about">About Us</option>
                                            <option value="faq">FAQ</option>
                                            <option value="custom">Custom Page</option>
                                        </select>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Status
                                        </label>
                                        <select
                                            name="status"
                                            value={formData.status}
                                            onChange={handleChange}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                                        >
                                            <option value="draft">Draft</option>
                                            <option value="active">Active</option>
                                            <option value="inactive">Inactive</option>
                                        </select>
                                    </div>
                                </div>
                            </div>

                            {/* Content */}
                            <div className="space-y-4">
                                <h3 className="text-lg font-medium text-gray-900 border-b pb-2">
                                    Content *
                                </h3>
                                <div className={`${errors.content ? 'border border-red-500 rounded-md p-1' : ''}`}>
                                    <Editor
                                        apiKey={process.env.REACT_APP_TINYMCE_API_KEY}
                                        value={formData.content}
                                        onEditorChange={handleEditorChange}
                                        init={editorConfig}
                                    />
                                </div>
                                {errors.content && <p className="text-red-500 text-xs mt-1">{errors.content}</p>}
                            </div>
                        </form>
                    </div>

                    {/* Footer */}
                    <div className="border-t border-gray-200 p-6 mb-20">
                        <div className="flex space-x-3">
                            <button
                                type="button"
                                onClick={handleSubmit}
                                className="flex-1 bg-primary-600 text-white py-2 px-4 rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 transition-colors font-medium flex items-center justify-center"
                            >
                                <FiSave className="mr-2" />
                                {isEditMode ? 'Update Page' : 'Create Page'}
                            </button>
                            <button
                                type="button"
                                onClick={handleClose}
                                className="flex-1 bg-gray-200 text-gray-800 py-2 px-4 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors font-medium"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default OffCanvasForm;